function inlineSearch() {
    const form = document.querySelector('[data-inline-search]');
    const panel = document.querySelector('[data-search-results]');
    if (!form || !panel) return;

    const input = form.querySelector('input');
    const clear = form.querySelector('[data-search-clear]');
    const container = panel.closest('.gh-container');
    const originalFeed = panel.parentElement.querySelector(':scope > .gh-feed');
    const heading = container.querySelector('.gh-container-title');
    const originalHeading = heading.textContent;
    const feed = panel.querySelector('.gh-feed');
    const status = panel.querySelector('[data-search-status]');
    const retry = panel.querySelector('[data-search-retry]');
    const more = panel.querySelector('[data-search-more]');
    const template = panel.querySelector('template');
    const labels = panel.dataset;
    const pageSize = Math.max(1, Number(labels.pageSize) || 12);
    const normalize = value => String(value || '').normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase();
    let indexPromise;
    let revision = 0;
    let debounce;
    let matches = [];
    let shown = 0;

    function safeUrl(value, local = false) {
        if (typeof value !== 'string' || !value) return null;
        try {
            const url = new URL(value, location.href);
            return ['https:', 'http:'].includes(url.protocol) && (!local || url.origin === location.origin) ? url.href : null;
        } catch {
            return null;
        }
    }

    async function loadIndex() {
        if (indexPromise) return indexPromise;
        indexPromise = (async () => {
            const script = document.querySelector('script[data-key][data-sodo-search], script[data-key][src*="sodo-search"]');
            if (!script) throw new Error('search-unavailable');
            const host = new URL(script.dataset.host || location.origin);
            if (host.origin !== location.origin) throw new Error('search-origin');
            host.pathname = host.pathname.replace(/\/?$/, '/');
            const endpoint = new URL('ghost/api/content/posts/', host);
            endpoint.searchParams.set('key', script.dataset.key);
            endpoint.searchParams.set('limit', '100');
            endpoint.searchParams.set('include', 'tags,authors');
            endpoint.searchParams.set('fields', 'id,title,url,excerpt,custom_excerpt,published_at,feature_image,feature_image_alt,visibility,access,featured');
            const posts = new Map();
            let page = 1;
            let total;
            while (page) {
                endpoint.searchParams.set('page', page);
                const controller = new AbortController();
                const timeout = setTimeout(() => controller.abort(), 15000);
                let data;
                try {
                    const response = await fetch(endpoint, {signal: controller.signal, redirect: 'error', credentials: 'same-origin'});
                    if (!response.ok) throw new Error('search-request-failed');
                    data = await response.json();
                } finally {
                    clearTimeout(timeout);
                }
                const pagination = data.meta && data.meta.pagination;
                if (!Array.isArray(data.posts) || !pagination || !Number.isSafeInteger(pagination.total) || pagination.total < 0) throw new Error('search-response');
                if (total === undefined) total = pagination.total;
                if (pagination.total !== total) throw new Error('search-index-changed');
                for (const post of data.posts) {
                    if (typeof post.id !== 'string' || typeof post.title !== 'string' || !safeUrl(post.url, true) || !Number.isFinite(Date.parse(post.published_at))) throw new Error('search-post');
                    const tags = (post.tags || []).filter(tag => tag.visibility === 'public');
                    const summary = post.visibility === 'public' ? [post.custom_excerpt, post.excerpt].filter(Boolean).join(' ') : '';
                    posts.set(post.id, {...post, tags, searchText: normalize([post.title, summary, ...tags.map(tag => tag.name), ...(post.authors || []).map(author => author.name)].join(' '))});
                }
                const next = pagination.next;
                if (next !== null && (!Number.isSafeInteger(next) || next <= page || data.posts.length === 0)) throw new Error('search-pagination');
                page = next;
            }
            if (posts.size !== total) throw new Error('search-index-incomplete');
            return [...posts.values()].sort((a, b) => Date.parse(b.published_at) - Date.parse(a.published_at) || a.id.localeCompare(b.id));
        })().catch(error => {
            indexPromise = null;
            throw error;
        });
        return indexPromise;
    }

    function card(post) {
        const article = template.content.firstElementChild.cloneNode(true);
        const link = article.querySelector('[data-result-link]');
        link.textContent = post.title;
        link.href = safeUrl(post.url, true);
        const tag = article.querySelector('[data-result-tag]');
        tag.textContent = post.tags[0] ? post.tags[0].name : '';
        tag.hidden = !tag.textContent;
        const excerpt = article.querySelector('[data-result-excerpt]');
        excerpt.textContent = post.custom_excerpt || (post.visibility === 'public' ? post.excerpt : '') || '';
        excerpt.hidden = !excerpt.textContent;
        const image = article.querySelector('img');
        const imageUrl = post.feature_image && safeUrl(post.feature_image);
        if (imageUrl) {
            image.src = imageUrl;
            image.alt = post.feature_image_alt || post.title;
        } else {
            image.parentElement.remove();
        }
        if (!imageUrl || labels.showImages !== 'true') article.classList.add('no-image');
        if (post.featured) article.classList.add('featured');
        const author = article.querySelector('[data-result-author]');
        author.textContent = labels.by.replace('{authors}', (post.authors || []).map(item => item.name).join(', '));
        if (labels.showAuthor !== 'true' || !(post.authors || []).length) author.remove();
        const date = article.querySelector('time');
        date.dateTime = post.published_at;
        const locale = document.documentElement.lang || 'en';
        try {
            date.textContent = new Intl.DateTimeFormat(locale.startsWith('en') ? 'en-GB' : locale, {day: '2-digit', month: 'short', year: 'numeric'}).format(new Date(post.published_at));
        } catch {
            date.textContent = post.published_at.slice(0, 10);
        }
        date.hidden = labels.showDate !== 'true';
        const lock = article.querySelector('[data-result-lock]');
        if (post.access === true || post.visibility === 'public') lock.remove();
        else lock.setAttribute('aria-label', labels.restricted);
        return article;
    }

    function appendResults(focusNew = false) {
        const fragment = document.createDocumentFragment();
        matches.slice(shown, shown + pageSize).forEach(post => fragment.appendChild(card(post)));
        shown = Math.min(shown + pageSize, matches.length);
        const first = fragment.firstElementChild;
        feed.appendChild(fragment);
        if (focusNew && first) first.querySelector('a').focus();
        more.hidden = shown >= matches.length;
    }

    function updateUrl(query) {
        const url = new URL(location.href);
        if (query) url.searchParams.set('q', query);
        else url.searchParams.delete('q');
        history.replaceState(history.state, '', url);
    }

    function prepare() {
        const query = input.value.trim();
        revision += 1;
        clearTimeout(debounce);
        clear.hidden = !input.value;
        panel.hidden = !query;
        container.classList.toggle('is-searching', !!query);
        originalFeed.hidden = !!query;
        heading.textContent = query ? labels.heading : originalHeading;
        feed.replaceChildren();
        more.hidden = true;
        retry.hidden = true;
        feed.setAttribute('aria-busy', query ? 'true' : 'false');
        status.textContent = query ? labels.loading : '';
        updateUrl(query);
        return {query, current: revision};
    }

    async function search(query, current) {
        if (!query) return;
        try {
            const posts = await loadIndex();
            if (current !== revision) return;
            const terms = normalize(query).split(/\s+/).filter(Boolean);
            matches = posts.filter(post => terms.every(term => post.searchText.includes(term)));
            shown = 0;
            appendResults();
            status.textContent = matches.length ? new Intl.NumberFormat(document.documentElement.lang || 'en').format(matches.length) + ' ' + (matches.length === 1 ? labels.result : labels.results) : labels.empty;
        } catch {
            if (current !== revision) return;
            status.textContent = labels.error;
            retry.hidden = false;
        } finally {
            if (current === revision) feed.setAttribute('aria-busy', 'false');
        }
    }

    input.addEventListener('input', () => {
        const {query, current} = prepare();
        debounce = setTimeout(() => search(query, current), 200);
    });
    form.addEventListener('submit', event => {
        event.preventDefault();
        const {query, current} = prepare();
        search(query, current);
    });
    clear.addEventListener('click', () => {
        input.value = '';
        prepare();
        input.focus();
    });
    input.addEventListener('keydown', event => {
        if (event.key === 'Escape' && input.value) {
            event.preventDefault();
            clear.click();
        }
    });
    retry.addEventListener('click', () => {
        const {query, current} = prepare();
        input.focus();
        search(query, current);
    });
    more.addEventListener('click', () => appendResults(true));
    function restoreQuery() {
        input.value = new URL(location.href).searchParams.get('q') || '';
        const {query, current} = prepare();
        search(query, current);
    }
    window.addEventListener('popstate', restoreQuery);
    window.addEventListener('pageshow', event => {
        if (event.persisted) restoreQuery();
    });
    input.disabled = false;
    restoreQuery();
}
